<template>
  <v-dialog
    v-model="dialogState"
    persistent
    scrollable
    max-width="1800"
  >
    <v-card ref="recordEditorCreateDialogCard">
      <v-toolbar
        color="secondary"
        dark
        tile
      >
        <v-toolbar-title>
          Create A New Record
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          v-if="!isLoading"
          icon
          @click="_ => onCloseDialog()"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <template v-if="isLoading">
        <div
          class="d-flex align-center justify-center"
          style="min-height: 350px;"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            size="64"
          />
        </div>
      </template>

      <template v-else>
        <v-tabs
          v-model="selectedTab"
          active-class="active-tab"
          background-color="secondary"
          class="editor-tabs"
          dark
          grow
          hide-slider
          icons-and-text
        >
          <v-tab
            v-for="tab of enabledTabs"
            :key="tab.value"
            :disabled="tabDisabledFlag(tab.value)"
            :tab-value="tab.value"
            @click.native.prevent.stop.capture="_ => onTabClick(tab)"
          >
            {{ tab.label }}
            <v-badge
              bordered
              bottom
              :color="getTabBadgeColor(tab)"
              :offset-x="14"
              :offset-y="18"
              overlap
              :value="tab.errors || tab.warnings"
            >
              <template #badge>
                <v-icon>{{ getTabBadgeIcon(tab) }}</v-icon>
              </template>

              <v-icon large>
                {{ tab.icon }}
              </v-icon>
            </v-badge>
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-card-text
          ref="dialogCardContent"
          class="pa-2 ma-0"
          :style="cardContentStyle"
        >
          <v-tabs-items
            v-model="selectedTab"
          >
            <v-tab-item
              eager
              value="recordOverview"
            >
              <RecordOverview
                ref="recordOverview"
                :record="edited"
                :valid.sync="isRecordOverviewValid"
              />
            </v-tab-item>

            <v-tab-item
              eager
              value="legalCaseOverview"
            >
              <LegalCaseOverview
                ref="legalCaseOverview"
                :valid.sync="isLegalCaseOverviewValid"
                :reset-triggered="resetTriggered"
                is-create
                @selected="onRecordTeamSelection"
              />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="parties"
            >
              <Parties
                ref="parties"
                create-record
                :valid.sync="arePartiesValid"
              />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="recordHolderParty"
            >
              <RecordHolderPartySelect />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="checklist"
            >
              <ChecklistEditor
                ref="checklist"
                :checklist="editedLegalCase.checklist"
                :edited-checklist.sync="editedChecklist"
                :valid.sync="isChecklistValid"
                disable-custom-list
              >
                <template #title>
                  Case Objectives Checklist
                </template>

                <template #subtitle>
                  <p>Select a checklist to track case objectives or milestones for case closure enforcement</p>
                </template>
              </ChecklistEditor>
            </v-tab-item>

            <v-tab-item
              eager
              value="workflow"
            >
              <Workflow
                ref="workflow"
                :edited-workflow.sync="editedWorkflow"
                :valid.sync="isWorkflowValid"
                :workflow="editedLegalCase.workflow"
              />
            </v-tab-item>

            <v-tab-item
              :eager="true"
              value="engagement_letter"
            >
              <EngagementLetter
                ref="engagement_letter"
                :valid.sync="isEngagementLetterValid"
              />
            </v-tab-item>
          </v-tabs-items>

          <small class="d-flex">* indicates required field</small>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <div class="w-50 d-flex">
            <v-btn
              text
              @click="_ => onCloseDialog()"
            >
              Cancel
            </v-btn>
            <v-spacer />
            <v-btn
              :disabled="isFirstTabSelected"
              text
              @click="_ => gotoPreviousTab()"
            >
              <v-icon left>
                mdi-chevron-left
              </v-icon>
              {{ previousTabName }}
            </v-btn>
          </div>

          <div class="w-50 d-flex">
            <v-btn
              :disabled="isLastTabSelected"
              text
              @click="_ => gotoNextTab()"
            >
              {{ nextTabName }}
              <v-icon right>
                mdi-chevron-right
              </v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              color="warning"
              :disabled="!wasChanged"
              text
              @click="_ => onReset()"
            >
              <v-icon left>
                mdi-restore
              </v-icon>
              Reset
            </v-btn>
            <v-btn
              color="success"
              :disabled="!canSave"
              @click="_ => onSave()"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              Save
            </v-btn>
          </div>
        </v-card-actions>
      </template>

      <div
        v-if="isSaving"
        class="overlay-parent d-flex align-center justify-center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="128"
        />
      </div>
    </v-card>

    <ConfirmationDialog
      v-model="isConfirmSaveDialogVisible"
      title="Action Confirmation"
      cancel-button-label="Go Back"
      confirm-button-label="Save Anyway"
      @confirmed="_ => onSave(true)"
    >
      <template #message>
        <p>
          You haven't opened some tabs, do you want to check out any of them before saving?
        </p>
        <div class="pa-4">
          <div
            v-for="tab of unvisitedTabs"
            :key="tab.value"
            class="d-flex justify-center pa-2"
          >
            <v-btn
              depressed
              small
              @click="_ => openTab(tab.value)"
            >
              <v-icon left>
                {{ tab.icon }}
              </v-icon>
              Go to {{ tab.label }}
            </v-btn>
          </div>
        </div>
      </template>
    </ConfirmationDialog>

    <ConfirmationDialog
      v-model="isResetConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to reset all data? All your changes will be lost!"
      cancel-button-label="No"
      cancel-button-color="primary"
      confirm-button-label="Yes"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="_ => reset()"
    />

    <ConfirmationDialog
      v-model="isCloseDialogConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to exit? All your changes will be lost!"
      cancel-button-label="Stay"
      cancel-button-color="primary"
      confirm-button-label="Exit, don't save"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="_ => closeDialog()"
    />
  </v-dialog>
</template>

<script>
import { cloneDeep, uniqueId } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import { alertHandler } from '@/alertHandler'
import { deleteReturn } from '@/utilities'
import { filterValidParties } from '@/utilities/party'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { RECORD_NAMESPACE, virginRecord } from '@/store/record'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import ChecklistEditor from '@/components/Checklist/ChecklistEditor.vue'
import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import EngagementLetter from '@/components/LegalCase/Editor/LegalCaseEditorEngagementLetter.vue'
import LegalCaseOverview from '@/components/LegalCase/Editor/LegalCaseEditorOverview.vue'
import Parties from '@/components/LegalCase/Editor/LegalCaseEditorParties.vue'
import RecordHolderPartySelect from '@/components/Record/Editor/RecordHolderPartySelect.vue'
import RecordOverview from '@/components/Record/Editor/RecordEditorOverview.vue'
import Workflow from '@/components/LegalCase/Editor/LegalCaseEditorWorkflow.vue'

export default {
  name: 'RecordEditorCreateDialog',

  components: {
    ChecklistEditor,
    ConfirmationDialog,
    EngagementLetter,
    LegalCaseOverview,
    Parties,
    RecordHolderPartySelect,
    RecordOverview,
    Workflow,
  },

  model: {
    prop: 'visible',
    event: 'change',
  },

  props: {
    record: {
      default: _ => cloneDeep(virginRecord),
      type: Object,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      DEFAULT_SELECTED_TAB: 'recordOverview',
      cardContentHeight: 350,
      editedChecklist: null,
      editedWorkflow: {},
      isCloseDialogConfirmationDialogVisible: false,
      isConfirmSaveDialogVisible: false,
      isLoading: true,
      isResetConfirmationDialogVisible: false,
      isSaving: false,
      resetTriggered: false,
      selectedTab: null,
      tabList: [
        {
          enabled: true,
          errors: false,
          icon: 'mdi-briefcase-variant-outline',
          label: 'Record Overview',
          value: 'recordOverview',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-folder-account-outline',
          label: 'Case Overview',
          value: 'legalCaseOverview',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-account-group-outline',
          label: 'Parties',
          value: 'parties',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-account-star',
          label: 'Record Holder',
          value: 'recordHolderParty',
          visited: false,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'mdi-format-list-checks',
          label: 'Objectives',
          value: 'checklist',
          visited: false,
          warnings: false,
        },
        {
          enabled: false,
          errors: false,
          icon: 'mdi-sitemap',
          label: 'Workflow',
          value: 'workflow',
          visited: this.userCanEditWorkflows,
          warnings: false,
        },
        {
          enabled: true,
          errors: false,
          icon: 'forward_to_inbox',
          label: 'Engagement Letter',
          value: 'engagement_letter',
          visited: false,
          warnings: false,
        },
      ],
    }
  },

  computed: {
    arePartiesValid: {
      get () { return !this.getTabErrorStatus('parties') },
      set (val) { this.setTabErrorStatus('parties', !val) },
    },

    canSave () {
      const allTabsValid = !this.enabledTabs.find(et => et.errors || et.warnings)
      const allTabsVisited = !this.enabledTabs.find(et => !et.visited)
      const blocked = !allTabsVisited
      return this.wasChanged && allTabsValid && !blocked
    },

    cardContentStyle () {
      return `min-height: ${this.cardContentHeight}px`
    },

    dialogState: {
      get () {
        return this.createRecordDialogOpen
      },

      set (newValue) {
        if (newValue === this.dialogState) return

        this.setCreateRecordDialog(newValue)
      },
    },

    enabledTabs () {
      return this.tabList.filter(tab => tab.enabled)
    },

    hasUnvisitedTabs () {
      return !!this.unvisitedTabs.length
    },

    isChecklistValid: {
      get () { return !this.getTabErrorStatus('checklist') },
      set (val) { this.setTabErrorStatus('checklist', !val) },
    },

    isEngagementLetterValid: {
      get () { return !this.getTabErrorStatus('engagement_letter') },
      set (val) { this.setTabErrorStatus('engagement_letter', !val) },
    },

    isFirstTabSelected () {
      return this.selectedTab === this.enabledTabs[0].value
    },

    isLastTabSelected () {
      return this.selectedTab === this.enabledTabs[this.enabledTabs.length - 1].value
    },

    isLegalCaseOverviewValid: {
      get () { return !this.getTabErrorStatus('legalCaseOverview') },
      set (val) { this.setTabErrorStatus('legalCaseOverview', !val) },
    },

    isRecordOverviewValid: {
      get () { return !this.getTabErrorStatus('recordOverview') },
      set (val) { this.setTabErrorStatus('recordOverview', !val) },
    },

    isWorkflowValid: {
      get () { return !this.getTabErrorStatus('workflow') },
      set (val) { this.setTabErrorStatus('workflow', !val) },
    },

    legalCaseType () {
      return this.legalCaseTypes.find(lt => lt.id === this.editedLegalCase?.legal_case_type_id)
    },

    nextTabName () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (idx < this.enabledTabs.length - 1) return this.enabledTabs[idx + 1].label
      return 'Next'
    },

    previousTabName () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (idx > 0) return this.enabledTabs[idx - 1].label
      return 'Back'
    },

    unvisitedTabs () {
      return this.enabledTabs.filter(et => !et.visited)
    },

    ...mapGetters([
      'createRecordDialogOpen',
    ]),

    // eslint-disable-next-line llp/vuex-use-arrays
    ...mapGetters(LEGAL_CASE_NAMESPACE, {
      editedLegalCase: 'edited',
    }),

    ...mapGetters([
      'legalCaseTypes',
    ]),

    ...mapGetters(RECORD_NAMESPACE, [
      'currentRecord',
      'edited',
      'wasChanged',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'userCanEditWorkflows',
    ]),
  },

  watch: {
    editedLegalCase: {
      deep: true,

      handler () {
        this.calculateTabsVisibility()
        if (!this.editedLegalCase?.parties) return

        const recordHolderIdentifier = this.edited?.parties?.find(rp => rp.is_record_holder)?.identifier

        const parties = filterValidParties(this.editedLegalCase.parties).
          map(party => {
            return {
              ...party,
              identifier: party.identifier ?? uniqueId(),
              is_record_holder: party.identifier === recordHolderIdentifier,
            }
          })

        this.setEdited({
          ...this.edited,
          parties,
        })
      },

      immediate: true,
    },

    selectedTab: {
      handler (newVal, oldVal) {
        const tab = this.tabList.find(tl => tl.value === newVal)
        if (tab) tab.visited = true

        this.validateTab(oldVal)

        const newTab = this.$refs[newVal]
        if (newTab?.onTabOpened) newTab.onTabOpened()
      },

      immediate: true,
    },
  },

  created () {
    const newRecord = {
      ...this.record,
      opened: moment().format('YYYY-MM-DD'),
    }

    this.setEdited(newRecord)
    this.setOriginal(newRecord)
    this.setEditedLegalCase(newRecord?.legalCase)
    this.calculateTabsVisibility()
    this.isLoading = false
  },

  methods: {
    calculateTabsVisibility () {
      const selectedTab = this.tabList.find(tab => tab.value === this.selectedTab)
      if (!selectedTab?.enabled) this.selectedTab = this.DEFAULT_SELECTED_TAB

      const workflowTab = this.tabList.find(tab => tab.value === 'workflow')
      workflowTab.enabled = !this.editedLegalCase?.id && !!this.legalCaseType?.workflows?.length
    },

    closeDialog () {
      this.dialogState = false
    },

    getTabBadgeColor (tab) {
      if (tab.errors) return 'error'
      if (tab.warnings) return 'warning'
    },

    getTabBadgeIcon (tab) {
      if (tab.errors) return 'mdi-close-thick'
      if (tab.warnings) return 'mdi-exclamation-thick'
    },

    getTabClasses (tab) {
      if (tab.value === this.selectedTab) return 'primary'
      if (tab.errors) return 'tab-error'
      if (tab.warnings) return 'tab-warning'
      return 'transparent'
    },

    getTabErrorStatus (tab) {
      return this.tabList.find(t => t.value === tab).errors
    },

    getTabIcon (tab) {
      if (tab.errors) return 'mdi-close-octagon-outline'
      if (tab.warnings) return 'mdi-alert-outline'
      return tab.icon
    },

    getTabWarningStatus (tab) {
      return this.tabList.find(t => t.value === tab).warnings
    },

    goToCase (caseId) {
      this.$router.push(`/record/${this.currentRecord.id}/case/${caseId}`)
    },

    gotoNextTab () {
      if (!this.validateTab(this.selectedTab)) return
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)
      if (idx < this.enabledTabs.length - 1) this.selectedTab = this.enabledTabs[idx + 1].value
    },

    gotoPreviousTab () {
      const idx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)

      if (this.enabledTabs[idx].errors || this.enabledTabs[idx].warnings) return

      if (idx > 0) {
        this.selectedTab = this.enabledTabs[idx - 1].value
      }
    },

    onCloseDialog () {
      if (this.wasChanged) {
        this.isCloseDialogConfirmationDialogVisible = true
      } else {
        this.closeDialog()
      }
    },

    onRecordTeamSelection (teamSelection) {
      this.edited.team_ids = teamSelection.teamIds
    },

    onReset () {
      if (this.wasChanged) this.isResetConfirmationDialogVisible = true
    },

    onSave (confirmed = false) {
      if (this.hasUnvisitedTabs && !confirmed) {
        this.isConfirmSaveDialogVisible = true
        return
      }

      this.editedLegalCase.checklist = this.editedChecklist.items.length ? this.editedChecklist : null
      this.editedLegalCase.workflow = this.editedWorkflow

      this.isSaving = true
      this.save()
    },

    onTabClick (newTab) {
      if (newTab.value === this.selectedTab) return
      if (!this.validateTab(this.selectedTab)) return
      this.selectedTab = newTab.value
    },

    openTab (tab) {
      this.selectedTab = tab
    },

    reset () {
      this.resetTriggered = false
      this.resetEdited().
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error), error)).
        finally(_ => {
          this.isResetConfirmationDialogVisible = false

          this.tabList.forEach(tab => { tab.visited = false })
          const firstTab = this.tabList.find(tab => tab.enabled)
          this.selectedTab = firstTab.value
          this.resetTriggered = true
        })
    },

    save () {
      this.editedLegalCase.opened = deleteReturn(this.edited, 'opened')

      this.createRecordModelWithPartyAndCase({
        record: {
          ...this.edited,
          legal_case: this.editedLegalCase,
          public_id: { alphabet: this.edited.public_id_alphabet },
        },
      }).
        then(async record => {
          this.setRecord(record)
          this.goToCase(record.legal_cases[0].id)
          this.closeDialog()
          this.displayAlert('success', 'Legal Case saved successfully.')
        }).
        catch(error => this.displayAlert('error', parseErrorResponseMessage(error), error)).
        finally(_ => {
          this.isConfirmSaveDialogVisible = false
          this.isSaving = false
        })
    },

    setTabErrorStatus (tab, status) {
      this.tabList.find(t => t.value === tab).errors = status
    },

    setTabWarningStatus (tab, status) {
      this.tabList.find(t => t.value === tab).warnings = status
    },

    tabDisabledFlag (tabValue) {
      const tabIdx = this.enabledTabs.findIndex(et => et.value === tabValue)
      if (this.enabledTabs[tabIdx].visited) return false

      const selectedTabIdx = this.enabledTabs.findIndex(et => et.value === this.selectedTab)
      return tabIdx - 1 > selectedTabIdx
    },

    validateTab (tabValue) {
      const tab = this.enabledTabs.find(et => et.value === tabValue)
      if (!tab) return false

      const tabRef = this.$refs[tabValue]
      if (tabRef?.validate) tabRef.validate()

      return !(tab.errors || tab.warnings)
    },

    // eslint-disable-next-line llp/vuex-use-arrays
    ...mapActions(LEGAL_CASE_NAMESPACE, {
      setEditedLegalCase: 'setEdited',
    }),

    ...mapActions(RECORD_NAMESPACE, [
      'createRecordModelWithPartyAndCase',
      'resetEdited',
      'setCreateRecordDialog',
      'setEdited',
      'setOriginal',
      'setRecord',
    ]),
  },
}
</script>

<style scoped>
.active-tab {
  background-color: white;
  color: var(--v-secondary-base) !important;
}

.editor-tabs {
  .v-tab .v-badge {
    .v-badge__badge > .v-icon {
      font-size: 12px;
      color: white;
    }

    .v-badge__badge::after {
      border-color: white;
      border-width: 1.5px;
    }
  }

  .tab-error, .tab-warning {
    margin-top: 4px;
    margin-left: 2px;
    margin-right: 2px;
    background-color: transparent;
  }

  .tab-error {
    background-color: rgba(255, 0, 0, 0.2);
    color: var(--v-error-lighten1) !important;
  }

  .tab-warning {
    background-color: rgba(255, 127, 0, 0.2);
    color: var(--v-warning-lighten1) !important;
  }

  .tab-error .v-icon,
  .tab-warning .v-icon {
    color: inherit !important;
  }
}

.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
